import { Handle, Position } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import { useNodeStore } from '@/stores/node-store';
import { CONDITIONAL_EDGE_COLOR } from '@/constants/node-styling';
import { getHandleClassName } from '@/pages/workflow/helper/get-handle-class-name';
import {
  getControlTargetHandleId,
  isConditionalEdge,
} from '@/pages/workflow/helper/functional-control-flow';
import { isBranchHandle } from '@/pages/workflow/helper/switch-branch-sync';
import { cn } from '@/lib/utils';

interface ControlTargetHandleProps {
  nodeId: string;
  isLocked?: boolean;
}

/**
 * The landing point for a Switch branch: a control handle whose id is the
 * node id (the contract shared with the backend assembler). It is rendered
 * first inside the node so persisted conditional edges with an empty target
 * handle attach here, and it only becomes visible while a branch is being
 * dragged or once a branch is connected.
 */
export function ControlTargetHandle({
  nodeId,
  isLocked = false,
}: ControlTargetHandleProps) {
  const { activeConnect, hasIncomingBranch } = useNodeStore(
    useShallow((state) => ({
      activeConnect: state.activeConnect,
      hasIncomingBranch: state.edges.some(
        (edge) => edge.target === nodeId && isConditionalEdge(edge),
      ),
    })),
  );
  const nodes = useNodeStore((state) => state.nodes);
  const validateConnection = useNodeStore((state) => state.validateConnection);
  const isValidEdgeConnection = useNodeStore(
    (state) => state.isValidEdgeConnection,
  );

  const handleId = getControlTargetHandleId(nodeId);
  const branchDragActive =
    activeConnect?.side === 'source' &&
    isBranchHandle(activeConnect.handleId) &&
    activeConnect.nodeId !== nodeId;
  const visible = branchDragActive || hasIncomingBranch;

  return (
    <Handle
      id={handleId}
      type='target'
      position={Position.Left}
      isConnectable={!isLocked}
      isConnectableStart={false}
      title='Branch input: drop a Switch branch here'
      data-testid={`control-target-${nodeId}`}
      style={{
        top: 22,
        left: -7,
        transform: 'none',
        width: 14,
        height: 14,
        borderRadius: 9999,
        backgroundColor: CONDITIONAL_EDGE_COLOR,
        color: CONDITIONAL_EDGE_COLOR,
        border: '2px solid var(--color-card)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      className={cn(
        'control-target-handle transition-opacity duration-150',
        getHandleClassName({
          activeConnect,
          nodes,
          currentNodeId: nodeId,
          currentHandleId: handleId,
          handleType: 'target',
          validateConnection,
        }),
      )}
      isValidConnection={(edge) =>
        isValidEdgeConnection({
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
        })
      }
    />
  );
}
